import { useState, useEffect } from "react";
import { AdminField, AdminSaveButton, AdminSection } from "@/admin/AdminComponents";
import { Plus, Trash2, GripVertical } from "lucide-react";

const ICON_OPTIONS = ["Cloud", "Radio", "Monitor", "Cpu", "Box", "Server", "Shield", "Smartphone", "Layers", "Workflow"];

const ServicesEditor = ({ data, onSave, saving }) => {
  const [form, setForm] = useState({});

  useEffect(() => { setForm({ ...data }); }, [data]);

  const update = (key, val) => setForm((p) => ({ ...p, [key]: val }));
  const updateItem = (idx, key, val) => {
    const items = [...(form.items || [])];
    items[idx] = { ...items[idx], [key]: val };
    setForm((p) => ({ ...p, items }));
  };
  const removeItem = (idx) => {
    const items = [...(form.items || [])];
    items.splice(idx, 1);
    setForm((p) => ({ ...p, items }));
  };
  const addItem = () => {
    const items = [...(form.items || [])];
    items.push({ id: `service-${Date.now()}`, title: "", description: "", icon: "Cloud", span: "" });
    setForm((p) => ({ ...p, items }));
  };
  const moveItem = (idx, dir) => {
    const items = [...(form.items || [])];
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= items.length) return;
    [items[idx], items[newIdx]] = [items[newIdx], items[idx]];
    setForm((p) => ({ ...p, items }));
  };

  return (
    <div data-testid="services-editor">
      <AdminSection title="Section Header">
        <AdminField label="Section Label" value={form.label} onChange={(v) => update("label", v)} />
        <AdminField label="Heading" value={form.heading} onChange={(v) => update("heading", v)} />
      </AdminSection>

      <AdminSection title="Service Cards" description="Add, edit, reorder, or remove service cards">
        {(form.items || []).map((item, idx) => (
          <div key={item.id || idx} className="p-4 rounded-xl bg-white/[0.02] border border-white/[0.06] space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <button onClick={() => moveItem(idx, -1)} className="p-1 text-neutral-600 hover:text-white transition-colors" title="Move up">
                  <GripVertical className="w-3.5 h-3.5" />
                </button>
                <span className="text-xs text-neutral-500 font-['Manrope'] font-medium">Card {idx + 1}</span>
              </div>
              <button
                data-testid={`remove-service-${idx}`}
                onClick={() => removeItem(idx)}
                className="p-1.5 rounded-md text-neutral-600 hover:text-red-400 hover:bg-red-500/10 transition-colors"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
            <AdminField label="Title" value={item.title} onChange={(v) => updateItem(idx, "title", v)} />
            <AdminField label="Description" value={item.description} onChange={(v) => updateItem(idx, "description", v)} rows={2} />
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-neutral-500 font-['Manrope'] mb-2 uppercase tracking-wider">Icon</label>
                <select
                  value={item.icon || "Cloud"}
                  onChange={(e) => updateItem(idx, "icon", e.target.value)}
                  className="w-full px-4 py-3 rounded-xl bg-white/[0.04] border border-white/[0.08] text-white text-sm font-['Manrope'] focus:outline-none focus:border-white/[0.2]"
                >
                  {ICON_OPTIONS.map((ic) => <option key={ic} value={ic}>{ic}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-neutral-500 font-['Manrope'] mb-2 uppercase tracking-wider">Grid Span</label>
                <select
                  value={item.span || ""}
                  onChange={(e) => updateItem(idx, "span", e.target.value)}
                  className="w-full px-4 py-3 rounded-xl bg-white/[0.04] border border-white/[0.08] text-white text-sm font-['Manrope'] focus:outline-none focus:border-white/[0.2]"
                >
                  <option value="">Default (1 col)</option>
                  <option value="md:col-span-2">Wide (2 cols)</option>
                  <option value="lg:col-span-2">Wide on large</option>
                </select>
              </div>
            </div>
          </div>
        ))}

        <button
          data-testid="add-service-button"
          onClick={addItem}
          className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border border-dashed border-white/[0.1] text-sm text-neutral-500 font-['Manrope'] hover:border-white/[0.2] hover:text-white transition-colors duration-200"
        >
          <Plus className="w-4 h-4" /> Add Service Card
        </button>
      </AdminSection>

      <AdminSaveButton onClick={() => onSave(form)} saving={saving} />
    </div>
  );
};

export default ServicesEditor;
